import { Injectable } from "@angular/core";
import { OrderState, UserType, TradingGroupImpl } from "shared/cubiq-model-multi-asset";
import {
  ActiveUserProfilePromiseService,
  SystemConfigPromiseService,
  OrdersTableDefinitionFactory,
  ActiveUser,
  TraderGroupLookupService,
} from "shared/cubiq-services";
import { BfxOrderBlotterFilteringService } from "../bfx-blotter-orders/bfx-blotter-orders-filter.service";
import { BfxBlotterFilter } from "../bfx-blotter-filter/bfx-blotter-filter.constants";

const COLUMN_ORDER_STATE: string = "orderState";
const COLUMN_TRADER_GROUP: string = "deskId";
const COLUMN_TARGET_GROUP: string = "targetDeskId";
const COLUMN_IS_EXTERNAL: string = "isExternal";
const COLUMN_IS_TARGET_EXTERNAL: string = "isTargetExternal";

const CONFIG_DEFAULT_TRADER_GROUP: string = "DEFAULT_TRADER_GROUP_ID";
const DEFAULT_TRADER_GROUP: string = "-1";

const INBOX_ALLOWED_ORDER_STATES: Set<OrderState> = new Set([
  OrderState.ACCEPTANCE_NEEDED,
  OrderState.MODIFY_ACCEPTANCE_NEEDED,
  OrderState.CANCEL_ACCEPTANCE_NEEDED,
  OrderState.EXPIRY_ACCEPTANCE_NEEDED,
  OrderState.TRIGGERED_EXPIRY_ACCEPTANCE_NEEDED,
  OrderState.MIGRATION_ACCEPTANCE_NEEDED,
  OrderState.EXT_ACCEPTANCE_NEEDED,
  OrderState.EXT_CANCEL_ACCEPTANCE_NEEDED,
  OrderState.EXT_MODIFY_ACCEPTANCE_NEEDED,
]);

const INBOX_FORBIDDEN_ORDER_SATES: Set<OrderState> = new Set([
  OrderState.IN_MODIFICATION,
  OrderState.GAMMA_MID_PENDING,
]);

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { LimitOrder } from "shared/cubiq-services/model/orders/LimitOrder";

@Injectable()
export class BfxBlotterInboxFilter {
  private activeUserTradingGroups: Set<string>;
  private activeUserType: UserType = null;
  private defaultTraderGroupId: string = null;
  private ready: Promise<boolean> = null;
  private selectedFilters: Array<BfxBlotterFilter> = [];
  private missingTraderGroupSet = new Set();

  public updateFilterDataCallback: any;

  constructor(
    protected activeUserService: ActiveUserProfilePromiseService,
    protected systemConfigService: SystemConfigPromiseService,
    protected tableDefinitionFactory: OrdersTableDefinitionFactory,
    protected traderGroupService: TraderGroupLookupService,
    protected filteringService: BfxOrderBlotterFilteringService,
  ) {
    this.systemConfigService.getSystemConfig(CONFIG_DEFAULT_TRADER_GROUP).then((value: string) => {
      this.defaultTraderGroupId = value != null ? value : DEFAULT_TRADER_GROUP;
    });

    this.ready = new Promise((res, rej) => {
      this.activeUserService.getActiveUserData().then((value: ActiveUser) => {
        this.activeUserType = value.userTypeId;

        if (value.tradingGroupsIds) {
          this.activeUserTradingGroups = new Set(value.tradingGroupsIds);
        }
        res(true);
      });
    });
  }

  public isReady(): Promise<boolean> {
    return this.ready;
  }

  public doesFilterPass(rawData: LimitOrder, dealtMode: boolean): boolean {
    let isInbox: boolean = false;

    try {
      isInbox = this.doesInboxFilterPass(rawData);
      if (isInbox && this.selectedFilters.length > 0) {
        isInbox = this.filteringService.doFiltersPass(
          rawData as any,
          this.selectedFilters,
          false,
          dealtMode,
        );
      }
    } catch (e) {
      logger.out.error(
        `${this.constructor.name}: Error while checking order for inbox filters ${
          rawData != null ? rawData.orderId : "null"
        } ${e}`,
      );
    }

    return isInbox;
  }

  protected doesInboxFilterPass(rawData: LimitOrder): boolean {
    let orderState: OrderState = rawData.orderState;

    let traderGroupId: string = null;
    let isExternal: boolean = false;
    if (rawData.deskId != null) {
      let traderGroup: TradingGroupImpl = this.traderGroupService.getItem(
        rawData.deskId.toString(),
      );

      if (traderGroup != null) {
        traderGroupId = traderGroup.groupId.toString();
        isExternal = traderGroup.isExternallyFilledFg;
      } else if (this.missingTraderGroupSet.has(rawData?.deskId?.toString?.()) == false) {
        logger.out.warn(
          `${this.constructor.name}: Order ${rawData.orderId} has owner group id ${rawData.deskId} but trader group was not found.`,
        );
        logger.out.warn(
          `${this.constructor.name}: further warnings for owner group id ${rawData.deskId} will be suppressed`,
        );
        this.missingTraderGroupSet.add(rawData?.deskId?.toString?.());
      }
    }

    let targetGroupId: string = null;
    let isTargetExternal: boolean = false;
    if (rawData.targetDeskId != null) {
      let traderGroup: TradingGroupImpl = this.traderGroupService.getItem(
        rawData.targetDeskId.toString(),
      );

      if (traderGroup != null) {
        targetGroupId = traderGroup.groupId.toString();
        isTargetExternal = traderGroup.isExternallyFilledFg;
      } else if (this.missingTraderGroupSet.has(rawData?.targetDeskId?.toString?.()) == false) {
        logger.out.warn(
          `${this.constructor.name}: Order ${rawData.orderId} has target group id ${rawData.targetDeskId} but trader group was not found.`,
        );
        logger.out.warn(
          `${this.constructor.name}: further warnings for target group id ${rawData.targetDeskId} will be suppressed`,
        );
        this.missingTraderGroupSet.add(rawData?.targetDeskId?.toString?.());
      }
    }

    // Get target group
    let isAdmin: boolean = this.activeUserType == UserType.ADMIN;
    let isState: boolean = INBOX_ALLOWED_ORDER_STATES.has(orderState);
    let isStateForbidden: boolean = INBOX_FORBIDDEN_ORDER_SATES.has(orderState);

    let isMyTraderGroup: boolean =
      traderGroupId != null && (this.activeUserTradingGroups.has(traderGroupId) || isAdmin);
    let isTargetTraderGroup: boolean =
      targetGroupId != null && (this.activeUserTradingGroups.has(targetGroupId) || isAdmin);
    let isDefaultTraderGroup: boolean =
      traderGroupId != null && traderGroupId == this.defaultTraderGroupId;

    // OMS-31234 Pre Acceptance
    if (
      isMyTraderGroup &&
      isExternal &&
      (OrderState.ACCEPTANCE_NEEDED == orderState ||
        OrderState.CANCEL_ACCEPTANCE_NEEDED == orderState ||
        OrderState.MODIFY_ACCEPTANCE_NEEDED == orderState)
    )
      return true;

    // let isTypeAggregate: boolean = OrderDataAggregationType.isTypeAggregate(rawData[`as_sort_${COLUMN_AGGREGATION_KEY}`]);
    if (isExternal || isTargetExternal /* || isTypeAggregate */) return false;

    /* Initial fix for OMS-32320 r61043 (code below) is a direct cause for OMS-32847
        if (isState)
          return isMyTraderGroup || isDefaultTraderGroup || isTargetTraderGroup; 
        return false; */

    return isStateForbidden
      ? false
      : (isState && isMyTraderGroup) || isDefaultTraderGroup || isTargetTraderGroup;
  }

  public setSelectedFilters(selectedFilters: Array<BfxBlotterFilter>): void {
    this.selectedFilters = selectedFilters;
    if (this.updateFilterDataCallback != null) {
      this.updateFilterDataCallback();
    }
  }
}
